import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Alert, Snackbar, CircularProgress,
  Card, CardContent, Grid, FormControlLabel, InputAdornment, Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Speed as SpeedIcon,
  ListAlt as LogsIcon,
  BarChart as BarChartIcon,
  Warning as WarningIcon,
  DeleteSweep as ResetIcon,
} from '@mui/icons-material';
import AdminService from '../../services/AdminService';

const RateLimitManagement = () => {
  const [rateLimitConfig, setRateLimitConfig] = useState({ enabled: true, daily_limit: 500 });
  const [usageStats, setUsageStats] = useState([]);
  const [accessLogs, setAccessLogs] = useState({ logs: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logsError, setLogsError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editingLimit, setEditingLimit] = useState(false);
  const [newDailyLimit, setNewDailyLimit] = useState(500);
  const [logLimit, setLogLimit] = useState(100);

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ── Fetch ─────────────────────────────────────────────────────

  const fetchConfig = useCallback(async () => {
    try {
      const data = await AdminService.getRateLimitConfig();
      setRateLimitConfig(data);
      setNewDailyLimit(data.daily_limit);
    } catch (err) {
      console.error('Error fetching rate limit config:', err);
    }
  }, []);

  const fetchUsage = useCallback(async () => {
    try {
      const data = await AdminService.getApiKeyUsageStats();
      setUsageStats(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching usage stats:', err);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLogsError(null);
    try {
      const data = await AdminService.getApiAccessLogs(logLimit, null);
      setAccessLogs(data || { logs: [], total: 0 });
    } catch (err) {
      console.error('Error fetching access logs:', err);
      setLogsError(err?.message || String(err));
    }
  }, [logLimit]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchConfig(), fetchUsage(), fetchLogs()]);
    setLoading(false);
  }, [fetchConfig, fetchUsage, fetchLogs]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Actions ──────────────────────────────────────────────────

  const handleToggle = async () => {
    try {
      await AdminService.updateRateLimitConfig({ enabled: !rateLimitConfig.enabled });
      showSnackbar(`Rate limiting ${rateLimitConfig.enabled ? 'disabled' : 'enabled'}`);
      await fetchConfig();
    } catch {
      showSnackbar('Failed to toggle rate limiting', 'error');
    }
  };

  const handleResetCounters = async () => {
    if (!window.confirm('Reset all rate limit counters and clear access logs?')) return;
    try {
      await AdminService.resetRateLimitCounters();
      showSnackbar('Counters and access log cleared');
      await Promise.all([fetchUsage(), fetchLogs()]);
    } catch {
      showSnackbar('Failed to reset counters', 'error');
    }
  };

  const handleSaveLimit = async () => {
    try {
      await AdminService.updateRateLimitConfig({
        daily_limit: newDailyLimit,
        enabled: rateLimitConfig.enabled,
      });
      showSnackbar('Daily limit updated');
      setEditingLimit(false);
      await fetchConfig();
    } catch {
      showSnackbar('Failed to update daily limit', 'error');
    }
  };

  const getStatusColor = (status) => {
    const s = String(status);
    if (s === '429') return 'error';
    if (s.startsWith('4') || s.startsWith('5')) return 'error';
    return 'success';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>Rate Limiting</Typography>
          <Typography variant="body2" color="text.secondary">
            Control and monitor the global daily request limit across all users.
          </Typography>
        </Box>
        <IconButton onClick={fetchAll} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* Config Card */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SpeedIcon color="primary" />
          <Typography variant="h6">Configuration</Typography>
          <Chip
            label={rateLimitConfig.enabled ? 'Enabled' : 'Disabled'}
            color={rateLimitConfig.enabled ? 'success' : 'default'}
            size="small"
            variant="outlined"
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={rateLimitConfig.enabled}
                onChange={handleToggle}
                color="success"
              />
            }
            label={<Typography variant="body2">Rate limiting active</Typography>}
          />

          <Divider orientation="vertical" flexItem />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Global daily limit:</Typography>
            {editingLimit ? (
              <>
                <TextField
                  type="number"
                  size="small"
                  value={newDailyLimit}
                  onChange={(e) => setNewDailyLimit(Math.max(1, parseInt(e.target.value) || 1))}
                  sx={{ width: 120 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">/day</InputAdornment>,
                  }}
                />
                <Button size="small" variant="contained" onClick={handleSaveLimit}>Save</Button>
                <Button size="small" onClick={() => { setEditingLimit(false); setNewDailyLimit(rateLimitConfig.daily_limit); }}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Chip label={`${rateLimitConfig.daily_limit} requests/day`} color="primary" variant="outlined" />
                <Button size="small" onClick={() => setEditingLimit(true)}>Edit</Button>
              </>
            )}
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          Counters reset at midnight UTC. Applied to POST requests on all protected endpoints.
          Each question calls either <strong>/reasoning/stream</strong> (streaming mode) or <strong>/ask-agent</strong> (fallback mode), not both.
        </Typography>
      </Paper>

      {/* Usage Stats */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarChartIcon color="primary" />
            <Typography variant="h6">Today's Usage by Endpoint</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" startIcon={<RefreshIcon />} onClick={fetchUsage}>Refresh</Button>
            <Button size="small" color="error" startIcon={<ResetIcon />} onClick={handleResetCounters}>Reset</Button>
          </Box>
        </Box>

        {usageStats.length === 0 ? (
          <Alert severity="info">No usage data for today yet.</Alert>
        ) : (
          <Grid container spacing={2}>
            {usageStats.map((stat, idx) => {
              // Show per-endpoint usage instead of global total
              const endpoints = stat.endpoints || {};
              
              return Object.keys(endpoints).length === 0 ? (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
                  <Card variant="outlined">
                    <CardContent sx={{ pb: '12px !important' }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                        No usage today
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">Requests today</Typography>
                        <Typography variant="caption" fontWeight="bold">0 / {rateLimitConfig.daily_limit}</Typography>
                      </Box>
                      <Box sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 3, mt: 0.5, overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: '0%', borderRadius: 3, bgcolor: 'success.main' }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ) : Object.entries(endpoints).map(([endpoint, count]) => {
                const getEndpointLimit = (ep) => {
                  if (rateLimitConfig?.endpoint_limits) {
                    const key = ep.replace(/^\//, '').replace(/-/g, '_').replace(/\//g, '_');
                    if (rateLimitConfig.endpoint_limits[key]) return rateLimitConfig.endpoint_limits[key];
                    const baseKey = ep.replace(/^\//, '').split('/')[0].replace(/-/g, '_');
                    if (rateLimitConfig.endpoint_limits[baseKey]) return rateLimitConfig.endpoint_limits[baseKey];
                  }
                  return rateLimitConfig?.daily_limit || 500;
                };

                const endpointLimit = getEndpointLimit(endpoint);
                const pct = endpointLimit > 0 ? Math.min(100, (count / endpointLimit) * 100) : 0;
                
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`${idx}-${endpoint}`}>
                    <Card variant="outlined">
                      <CardContent sx={{ pb: '12px !important' }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                          {endpoint}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">Requests today</Typography>
                          <Typography variant="caption" fontWeight="bold">
                            {count} / {endpointLimit}
                          </Typography>
                        </Box>
                        <Box sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 3, mt: 0.5, overflow: 'hidden' }}>
                          <Box sx={{
                            height: '100%', width: `${pct}%`, borderRadius: 3,
                            bgcolor: pct > 80 ? 'error.main' : pct > 50 ? 'warning.main' : 'success.main',
                          }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              });
            })}
          </Grid>
        )}
      </Paper>

      {/* Access Logs */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        {logsError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLogsError(null)}>
            Failed to load access logs: {logsError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LogsIcon color="primary" />
            <Typography variant="h6">Access Logs</Typography>
            <Chip label={`${accessLogs.total || 0} total`} size="small" variant="outlined" />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              select size="small" value={logLimit}
              onChange={(e) => setLogLimit(Number(e.target.value))}
              sx={{ width: 90 }}
              SelectProps={{ native: true }}
            >
              {[50, 100, 250, 500].map(n => <option key={n} value={n}>{n}</option>)}
            </TextField>
            <Button size="small" startIcon={<RefreshIcon />} onClick={fetchLogs}>Refresh</Button>
          </Box>
        </Box>

        <TableContainer sx={{ maxHeight: 450 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><strong>Timestamp</strong></TableCell>
                <TableCell><strong>IP</strong></TableCell>
                <TableCell><strong>Endpoint</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Message</strong></TableCell>
                <TableCell><strong>Rate</strong></TableCell>
                <TableCell><strong>Duration</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(!accessLogs.logs || accessLogs.logs.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No access logs available.
                    </Typography>
                    {accessLogs._debug_csv_path && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontFamily: 'monospace' }}>
                        CSV: {accessLogs._debug_csv_path} | exists: {String(accessLogs._debug_file_exists)}{accessLogs._debug_file_exists ? ` | ${accessLogs._debug_file_size} bytes` : ''}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                accessLogs.logs.map((log, idx) => (
                  <TableRow key={idx} hover
                    sx={{ bgcolor: String(log.status) === '429' ? 'error.50' : 'inherit' }}
                  >
                    <TableCell>
                      <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{log.ip}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{log.endpoint}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={log.status} size="small"
                        color={getStatusColor(log.status)} variant="filled" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{log.message}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontWeight="bold">{log.rateLimit}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{log.duration}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Info */}
      <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <WarningIcon color="info" fontSize="small" sx={{ mt: 0.2 }} />
          <Typography variant="body2" color="text.secondary">
            Rate limits apply to POST requests on protected endpoints. All users share a single global daily limit.
            Health checks, static files, and admin panel requests are exempt. 
            Counters reset daily at <strong>midnight UTC</strong>. 
            All access is logged to <code>application_logs/api_access.csv</code>.
          </Typography>
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RateLimitManagement;